import {
  Button,
  HStack,
  Input as ChakraInput,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Spacer,
  Text as ChakraText,
  useBoolean,
  VStack,
} from '@chakra-ui/react'
import type { RefObject } from 'react'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { useTranslation } from 'react-i18next'
import { colors } from '../../../../ui-kit'
import useOutsideClickHandler from '../../../hooks/useOutsideClickHandler'
import type {
  CmFieldResponseObject,
  CustomMetadataResponse,
  FormValidationObject,
  RemoveFilterProps,
  SearchQueryJson,
} from '../../../interfaces'
import {
  extractSingleField,
  validateFilter,
} from '../../../../utils/mediaLibrary'
import AdvancedSearch from '../AdvancedSearch'
import SearchBarAutoCompleter from '../Search/SearchBarAutoCompleter'
import { MLContext } from '../../../providers'
import { MediaLibraryActions } from '../../../providers/MLProvider'

interface Props {
  searchInputRef: RefObject<any>
  searchInputActualRef: RefObject<any>
  nameValue: string | null
  customMetadataFields: CmFieldResponseObject[]
  inputBoxLeftElement: React.ReactElement
  inputBoxRightElement: React.ReactElement | null
  searchQueryJson: SearchQueryJson
  setNameValue(string): void
  setSearchQueryJson(searchQueryJson: SearchQueryJson): void
  onSubmitSearch(): void
  setSearchFocused(boolean): void
  onReset(): void
  onClose(): void
  customMetadataFieldsMapping: { [fieldName: string]: CustomMetadataResponse }
}

const SearchMenu = ({
  searchInputRef,
  searchInputActualRef,
  setSearchFocused,
  nameValue,
  inputBoxLeftElement,
  inputBoxRightElement,
  setNameValue,
  onSubmitSearch,
  customMetadataFields,
  customMetadataFieldsMapping,
  onReset,
  onClose,
  searchQueryJson,
  setSearchQueryJson,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const outsideClickRef = useRef(null)
  const maxWidthRef = useRef(null)
  const localInputRef = useRef<HTMLInputElement>(null)
  const [submitFlag, setSubmitFlag] = useState<boolean>(false)
  const [isNameInputDisabled, setIsNameInputDisabled] = useBoolean(false)
  const [{ openAdvancedSearch }, dispatch] = useContext(MLContext)

  const validationObject: FormValidationObject = useMemo(() => {
    let isValid = true
    const errors: Array<Array<string | null>> = []

    searchQueryJson.filterGroups.forEach((filterGroup, groupIndex) => {
      errors.push([])
      filterGroup.expressions.forEach((expression, filterIndex) => {
        const validation = validateFilter({
          expression,
          customMetadataField:
            customMetadataFieldsMapping[expression.fieldName],
        })
        if (!validation.isValid) {
          isValid = false
        }
        errors[groupIndex][filterIndex] = validation.error
      })
    })

    return {
      isValid,
      errors,
    }
  }, [searchQueryJson, customMetadataFieldsMapping])

  const shouldResetOpenAdvancedSearch = useCallback(
    () => !searchQueryJson.filterGroups.length,
    [searchQueryJson],
  )

  useOutsideClickHandler({
    ref: outsideClickRef,
    handler: () => {
      void setSearchFocused(false)
      if (shouldResetOpenAdvancedSearch()) {
        dispatch([
          {
            type: MediaLibraryActions.SET_OPEN_ADVANCED_SEARCH,
            payload: false,
          },
        ])
      }
    },
    dependencies: [shouldResetOpenAdvancedSearch],
  })

  const inputRect = useMemo<DOMRect | undefined>(
    () => searchInputRef.current?.getBoundingClientRect(),
    [searchInputRef],
  )

  const inputActualRect = useMemo<DOMRect | undefined>(
    () => searchInputActualRef.current?.getBoundingClientRect(),
    [searchInputActualRef],
  )

  const onRemoveField = (filter: RemoveFilterProps) => {
    const newSearchQueryJSON = { ...searchQueryJson }
    if (newSearchQueryJSON.filterGroups) {
      newSearchQueryJSON.filterGroups[filter.groupId].expressions.splice(
        filter.filterId,
        1,
      )
      const totalFilters =
        newSearchQueryJSON.filterGroups[filter.groupId].expressions.length
      if (totalFilters === 0) {
        newSearchQueryJSON.filterGroups.splice(filter.groupId, 1)
      }
    }
    setSearchQueryJson(newSearchQueryJSON)
  }

  const focusInput = useCallback(() => {
    if (localInputRef.current) {
      const isDisabled = localInputRef.current.disabled
      localInputRef.current.disabled = false
      localInputRef.current.focus()
      localInputRef.current.disabled = isDisabled
    }
  }, [localInputRef])

  useEffect(() => {
    setTimeout(() => void focusInput())
  }, [])

  useEffect(() => {
    const foundNameValue = extractSingleField(searchQueryJson, 'name')
    if (typeof foundNameValue !== 'undefined') {
      setIsNameInputDisabled.on()
      setNameValue('')
    } else {
      setIsNameInputDisabled.off()
    }
  }, [searchQueryJson])

  useEffect(() => {
    if (submitFlag) {
      setSubmitFlag(false)
      onSubmitSearch()
      void setSearchFocused(false)
    }
  }, [submitFlag])

  return openAdvancedSearch ? (
    ReactDOM.createPortal(
      <VStack
        ref={outsideClickRef}
        spacing="0"
        zIndex={1401}
        position="fixed"
        width="60vw"
        height="70vh"
        maxHeight="70vh"
        bgColor={colors.appWhite}
        top={inputRect?.top ? `${inputRect.top}px` : 'initial'}
        left={inputRect?.left ? `${inputRect.left}px` : 'initial'}
        borderRadius="0.25rem"
        borderWidth="1px"
        borderColor="#e6e7ea"
        overflow="auto"
      >
        <InputGroup>
          <InputLeftElement
            children={inputBoxLeftElement}
            position="static"
            width="unset"
            borderBottomWidth="1px"
            cursor="default"
            paddingLeft="0.5em"
          />
          <ChakraInput
            padding="0px"
            placeholder={t('collection', 'Collection')}
            ref={localInputRef}
            value={nameValue ?? ''}
            onChange={e => {
              setNameValue(e.target.value)
            }}
            onKeyPress={e => {
              if (e.key === 'Enter') {
                setSubmitFlag(true)
              }
            }}
            isDisabled={isNameInputDisabled}
            borderWidth="0"
            borderBottomWidth="1px"
            borderBottomColor="#e6e7ea"
            borderBottomRadius="none"
            focusBorderColor="none"
            _hover={{ borderColor: '#e6e7ea' }}
            position="static"
          />
          {inputBoxRightElement ? (
            <InputRightElement width="unset" children={inputBoxRightElement} />
          ) : null}
        </InputGroup>

        <VStack
          width="full"
          alignItems="start"
          spacing="1.5em"
          paddingX={12}
          paddingY="1.5em"
        >
          <VStack
            ref={maxWidthRef}
            width="full"
            alignItems="start"
            spacing="0.5em"
          >
            <ChakraText
              color={colors.appSlate}
              fontWeight="semibold"
              marginBottom="-0.5em"
            >
              {t('filters', 'Filters')}
            </ChakraText>

            <AdvancedSearch
              page="media-collection"
              searchQueryJson={searchQueryJson}
              setSearchQueryJson={setSearchQueryJson}
              customMetadataFields={
                customMetadataFields as CustomMetadataResponse[]
              }
              customMetadataFieldsMapping={customMetadataFieldsMapping}
              validationObject={validationObject}
              removeFilter={onRemoveField}
              maxWidthRef={maxWidthRef}
            />
          </VStack>
        </VStack>

        <Spacer />

        <HStack
          flexDirection="row-reverse"
          spacing="1em"
          paddingX={10}
          paddingY="1.5em"
          alignItems="start"
          width="full"
          borderTopWidth="1px"
          borderTopColor="#e6e7ea"
        >
          <Button
            onClick={() => {
              setSubmitFlag(true)
              if(shouldResetOpenAdvancedSearch()) {
                dispatch([
                  {
                    type: MediaLibraryActions.SET_OPEN_ADVANCED_SEARCH,
                    payload: false,
                  },
                ])
              }
            }}
            isDisabled={!validationObject.isValid}
            marginLeft="1em"
          >
            {t('search', 'Search')}
          </Button>

          <Button
            variant="outline"
            onClick={() => {
              onClose()
              if (shouldResetOpenAdvancedSearch()) {
                dispatch([
                  {
                    type: MediaLibraryActions.SET_OPEN_ADVANCED_SEARCH,
                    payload: false,
                  },
                ])
              }
            }}
          >
            {t('close', 'Close')}
          </Button>

          <Button variant="outline" color={colors.appBrick} onClick={onReset}>
            {t('reset', 'Reset')}
          </Button>
        </HStack>
      </VStack>,
      document.body,
    )
  ) : (
    <SearchBarAutoCompleter
      page="media-collection"
      outsideClickRef={outsideClickRef}
      nameValue={nameValue ?? ''}
      inputRect={inputRect}
      inputActualRect={inputActualRect}
      inputBoxRightElement={inputBoxRightElement}
      inputBoxLeftElement={inputBoxLeftElement}
      setNameValue={setNameValue}
      setSubmitFlag={setSubmitFlag}
      setSearchQueryJson={setSearchQueryJson}
      setSearchFocused={setSearchFocused}
    />
  )
}

export default SearchMenu
